import { paths } from "src/routes/paths";

export type ProviderOnboardingStatus = {
  profile: boolean;
  images: boolean;
  sports: boolean;
  venues: boolean;
  global_settings: boolean;
  services: boolean;
  tournaments: boolean;
};

export type ProviderOnboardingStepId = keyof ProviderOnboardingStatus;

export type ProviderOnboardingAction = {
  label: string;
  href: string;
};

export type ProviderOnboardingStep = {
  id: ProviderOnboardingStepId;
  label: string;
  description: string;
  href: string;
  reviewHref?: string;
  completed: boolean;
  actions?: ProviderOnboardingAction[];
};

export const isCalendarProvider = (providerType?: string | null) =>
  providerType === "facility" || providerType === "coach";

export const isOrganiserProvider = (providerType?: string | null) =>
  providerType === "organiser";

export const getRequiredOnboardingStepIds = (
  providerType?: string | null
): ProviderOnboardingStepId[] => {
  if (providerType === "association") return ["profile", "global_settings"];
  const baseSteps: ProviderOnboardingStepId[] = ["profile", "images", "sports"];

  if (isCalendarProvider(providerType)) {
    baseSteps.push("venues");
  }

  baseSteps.push("global_settings");

  if (isCalendarProvider(providerType)) {
    baseSteps.push("services");
  }

  if (isOrganiserProvider(providerType)) {
    baseSteps.push("tournaments");
  }

  return baseSteps;
};

export const getProviderOnboardingSteps = (
  status: ProviderOnboardingStatus,
  providerType?: string | null
): ProviderOnboardingStep[] => {
  const organiser = providerType === "organiser";

  const allSteps: Record<ProviderOnboardingStepId, ProviderOnboardingStep> = {
    profile: {
      id: "profile",
      label: "profile",
      description: "provider_onboarding_profile_description",
      href: providerType === "association" ? "/dashboard/association/profile" : organiser
        ? paths.dashboard.organiser.profile.data
        : paths.dashboard.profile.data,
      completed: status.profile,
    },
    images: {
      id: "images",
      label: "images",
      description: "provider_onboarding_images_description",
      href: paths.dashboard.profile.images,
      completed: status.images,
    },
    sports: {
      id: "sports",
      label: "sports",
      description: "provider_onboarding_sports_description",
      href: paths.dashboard.profile.sports,
      completed: status.sports,
    },
    venues: {
      id: "venues",
      label: "venues",
      description: "provider_onboarding_venues_description",
      href: paths.dashboard.profile.venues,
      completed: status.venues,
    },
    global_settings: {
      id: "global_settings",
      label: "global_settings",
      description: "provider_onboarding_global_settings_description",
      href: providerType === "association" ? "/dashboard/association/settings" : organiser
        ? paths.dashboard.organiser.settings.global
        : paths.dashboard.settings.global,
      completed: status.global_settings,
    },
    services: {
      id: "services",
      label: "admin_menu_services",
      description: "provider_onboarding_services_description",
      href: paths.dashboard.settings.pricing,
      completed: status.services,
      actions: [
        {
          label: "reservation",
          href: paths.dashboard.settings.venue.new,
        },
        {
          label: "training",
          href: paths.dashboard.settings.training.new,
        },
        {
          label: "passes",
          href: paths.dashboard.settings.passestickets.newpass,
        },
        {
          label: "tickets",
          href: paths.dashboard.settings.passestickets.newticket,
        },
      ],
    },
    tournaments: {
      id: "tournaments",
      label: "tournaments",
      description: "provider_onboarding_tournaments_description",
      href: paths.dashboard.organiser.tournament.new,
      reviewHref: paths.dashboard.organiser.tournaments,
      completed: status.tournaments,
    },
  };

  return getRequiredOnboardingStepIds(providerType).map((id) => allSteps[id]);
};
